import type { ContentBlock } from '@lp/shared';
import { Download, ExternalLink, FileText } from 'lucide-react';
import { Card, SectionHeading } from '../ui.js';
import { Markdown } from '../Markdown.js';
import { formatBytes } from '../../lib/format.js';

/**
 * Session notes: author-written markdown, downloadable files and outbound
 * links, in the order the author arranged them.
 */
export function NotesSection({ blocks }: { blocks: ContentBlock[] }) {
  if (!blocks.length) return null;

  const ordered = [...blocks].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <section aria-labelledby="session-notes">
      <SectionHeading id="session-notes">Notes</SectionHeading>
      <Card className="divide-y divide-line">
        {ordered.map((block) => (
          <div key={block.id} className="p-5">
            {block.kind === 'markdown' ? (
              <>
                {block.title ? (
                  <h3 className="font-semibold text-ink mb-2">{block.title}</h3>
                ) : null}
                <Markdown>{block.bodyMd}</Markdown>
              </>
            ) : block.kind === 'file' ? (
              <FileRow block={block} />
            ) : (
              <LinkRow block={block} />
            )}
          </div>
        ))}
      </Card>
    </section>
  );
}

function FileRow({ block }: { block: ContentBlock }) {
  const name = block.fileName ?? block.title ?? 'Attachment';
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden
        className="grid place-items-center h-10 w-10 shrink-0 rounded-control bg-surface-2 border border-line text-ink-muted"
      >
        <FileText size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-ink truncate">{block.title || name}</p>
        <p className="text-xs text-ink-subtle truncate">
          {name}
          {block.fileSize ? ` · ${formatBytes(block.fileSize)}` : ''}
        </p>
      </div>
      {block.downloadUrl ? (
        <a
          href={block.downloadUrl}
          download={name}
          className="inline-flex items-center gap-2 rounded-control border border-line-strong bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-surface-2 shrink-0"
        >
          <Download size={15} aria-hidden />
          Download
          <span className="sr-only"> {name}</span>
        </a>
      ) : (
        <span className="text-xs text-ink-subtle shrink-0">Link unavailable</span>
      )}
    </div>
  );
}

function LinkRow({ block }: { block: ContentBlock }) {
  if (!block.url) {
    return (
      <p className="text-sm text-ink-subtle">
        {block.title || 'Link'} — this link has no destination set.
      </p>
    );
  }
  return (
    <a
      href={block.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 group"
    >
      <span
        aria-hidden
        className="grid place-items-center h-10 w-10 shrink-0 rounded-control bg-surface-2 border border-line text-ink-muted"
      >
        <ExternalLink size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-ink group-hover:underline underline-offset-2 truncate">
          {block.title || block.url}
        </span>
        <span className="block text-xs text-ink-subtle truncate">{block.url}</span>
      </span>
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}
